import React, { useEffect, useState } from 'react';
import { Mail, ArrowLeft, Check, RefreshCw } from 'lucide-react';
import { SixDigitCodeInput } from './CustomSelect';
import { useLang } from './i18n';

type Props = {
  email: string;
  request: (path: string, method?: string, body?: unknown) => Promise<any>;
  onVerified: () => Promise<void>;
  onLogout: () => Promise<void>;
  mailEnabled: boolean;
};

export function VerifyEmail({ email, request, onVerified, onLogout, mailEnabled }: Props) {
  const [, , tr] = useLang();
  const [changing, setChanging] = useState(false), [newEmail, setNewEmail] = useState(''), [password, setPassword] = useState('');
  async function changeEmail(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const result = await request('/account/email', 'PATCH', { email: newEmail.trim(), password });
      setChanging(false);
      setPassword('');
      setNewEmail('');
      setCode('');
      setNote(result.verificationSent === false ? tr('Address changed. The code could not be sent yet, please resend it.', 'Adresse geändert. Der Code konnte noch nicht gesendet werden, bitte erneut senden.') : tr('Address changed. We sent a new code.', 'Adresse geändert. Wir haben einen neuen Code gesendet.'));
      await onVerified();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => {
    try {
      if (sessionStorage.getItem('malvo-verify-mail-failed') !== '1') return '';
      sessionStorage.removeItem('malvo-verify-mail-failed');
      const reason=sessionStorage.getItem('malvo-verify-mail-error')||'';
      sessionStorage.removeItem('malvo-verify-mail-error');
      if(reason.includes('Kontingent')) return tr('MALVO has reached its email sending allowance. Please try again later. Your account has been saved.', 'Das E-Mail-Kontingent von MALVO ist derzeit aufgebraucht. Bitte versuche es später erneut. Dein Konto bleibt erhalten.');
    } catch { return ''; }
    return tr('The verification email could not be sent. Please click "resend" to try again.', 'Die Bestätigungs-E-Mail konnte nicht gesendet werden. Klicke auf „erneut senden“, um es nochmal zu versuchen.');
  });
  const [note, setNote] = useState('');
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!seconds) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      await request('/auth/verify', 'POST', { token: code });
      await onVerified();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError('');
    try {
      await request('/auth/verify-request', 'POST');
      setNote(
        tr(
          'New verification code sent. Please also check your spam folder.',
          'Neuer Code gesendet. Prüfe auch deinen Spam-Ordner.'
        )
      );
      setSeconds(60);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-minimal verification-page">
      <header className="auth-topbar">
        <a className="auth-mark" href="/" aria-label="MALVO Chat">
          <img src="/icon.png" alt="MALVO" />
        </a>
        <span className="verification-brand">MALVO Chat</span>
        <button type="button" className="auth-top-login" onClick={() => void onLogout().catch(e => setError(e instanceof Error ? e.message : String(e)))}>
          <ArrowLeft size={12} />
          {tr('Switch Account', 'Anderes Konto')}
        </button>
      </header>
      <main className="verification-main">
        <section className="verification-card">
          <div className="verification-icon">
            <Mail size={26} />
          </div>
          <h1>{tr('Verify Email', 'E-Mail bestätigen')}</h1>
          <p>
            {tr('Enter the 6-digit code we sent to', 'Gib den sechsstelligen Code ein, den wir an')}
            <br />
            <strong>{email}</strong> {tr('', 'senden.')}
          </p>
          <form onSubmit={verify}>
            <SixDigitCodeInput value={code} onChange={setCode} disabled={busy} autoFocus />
            <button className="primary verification-submit-btn" disabled={busy || code.length !== 6}>
              <Check size={16} />
              {busy ? tr('Verifying…', 'Wird geprüft …') : tr('Verify Email', 'Bestätigen')}
            </button>
          </form>
          {error && (
            <p className="auth-feedback error" role="alert">
              {error}
            </p>
          )}
          {note && (
            <p className="auth-feedback success" role="status">
              {note}
            </p>
          )}
          <div className="verification-resend">
            <span>{tr("Didn't receive a code?", 'Keinen Code erhalten?')}</span>
            <button type="button" onClick={resend} disabled={busy || seconds > 0 || !mailEnabled}>
              <RefreshCw size={13} />
              {seconds
                ? tr(`Resend in ${seconds}s`, `Erneut senden in ${seconds}s`)
                : tr('Resend Code', 'Code erneut senden')}
            </button>
          </div>
          {changing ? (
            <form className="verification-change-email" onSubmit={changeEmail}>
              <label>{tr('New email address', 'Neue E-Mail-Adresse')}<input type="email" required maxLength={254} autoComplete="email" autoFocus value={newEmail} onChange={e => setNewEmail(e.target.value)} /></label>
              <label>{tr('Your password', 'Dein Passwort')}<input type="password" required maxLength={128} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} /></label>
              <div>
                <button type="button" className="secondary" disabled={busy} onClick={() => setChanging(false)}>{tr('Cancel', 'Abbrechen')}</button>
                <button className="primary" disabled={busy || !newEmail.trim() || !password}>{tr('Change address', 'Adresse ändern')}</button>
              </div>
            </form>
          ) : (
            <button type="button" className="verification-change-link" onClick={() => { setChanging(true); setError(''); setNote(''); }}>{tr('Wrong email address? Change it', 'Falsche E-Mail-Adresse? Jetzt ändern')}</button>
          )}
          {!mailEnabled && (
            <p className="auth-feedback">
              {tr(
                'Email delivery is temporarily unavailable.',
                'Der E-Mail-Versand ist derzeit nicht verfügbar.'
              )}
            </p>
          )}
        </section>
      </main>
      <footer className="auth-clean-footer">
        <span>© {new Date().getFullYear()} MALVO Chat</span>
        <span>{tr('Account Security', 'Kontosicherheit')}</span>
      </footer>
    </div>
  );
}
